import { useState } from 'react';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportIcon from '@mui/icons-material/BugReport';
import JiraTicketModal from './JiraTicketModal';
import { analyzeBugWithRegression } from '../../services/aiBugAnalysisService';

export default function AIBugAnalysisAccordion({ run }) {
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [analysisData, setAnalysisData] = useState(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  const handleAnalyzeBug = async () => {
    try {
      setIsAnalyzing(true);
      setAnalysisError('');

      const response = await analyzeBugWithRegression(run.runId);

      setAnalysisData(response);
    } catch (error) {
      setAnalysisError(error?.response?.data?.message || error.message || 'Failed to analyze bug.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <>
      <Accordion
        expanded={analysisExpanded}
        onChange={(_, expanded) => setAnalysisExpanded(expanded)}
        disableGutters
        elevation={0}
        sx={{
          mt: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <BugReportIcon color="error" />
            <Typography variant="subtitle2">AI Bug Analysis</Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          {!analysisData && !isAnalyzing && !analysisError && (
            <Box display="flex" justifyContent="flex-start">
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleAnalyzeBug}
                sx={{
                  mt: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.72rem',
                  px: 1.5,
                  py: 0.4,
                  minWidth: 120,
                  borderRadius: 1,
                  fontWeight: 600,
                }}
              >
                Analyze Bug
              </Button>
            </Box>
          )}

          {isAnalyzing && (
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          )}

          {analysisError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {analysisError}
            </Alert>
          )}

          {analysisData && (
            <Box display="flex" flexDirection="column" gap={2}>
              {isAnalyzing && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Refreshing AI analysis...
                </Alert>
              )}

              {/* Root Cause */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderLeft: '4px solid',
                  borderColor: 'error.main',
                }}
              >
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Root Cause
                </Typography>

                <Typography variant="body2">
                  {analysisData.root_cause || 'No root cause available.'}
                </Typography>
              </Paper>

              {/* Secondary Errors */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Secondary Errors
                </Typography>

                {analysisData.secondary_errors?.length ? (
                  <List dense>
                    {analysisData.secondary_errors.map((item, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemText primary={`• ${item}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No secondary errors.
                  </Typography>
                )}
              </Paper>

              {/* Possible Causes */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Possible Causes
                </Typography>

                {analysisData.possible_causes?.length ? (
                  <List dense>
                    {analysisData.possible_causes.map((item, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemText primary={`• ${item}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No possible causes available.
                  </Typography>
                )}
              </Paper>

              {/* Suggested Fixes */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Suggested Fixes
                </Typography>

                {analysisData.suggested_fixes?.length ? (
                  <List dense>
                    {analysisData.suggested_fixes.map((item, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemText primary={`• ${item}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No suggested fixes available.
                  </Typography>
                )}
              </Paper>

              {/* Action Buttons */}
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button
                  variant="outlined"
                  onClick={handleAnalyzeBug}
                  disabled={isAnalyzing}
                  size="small"
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    px: 1.5,
                    py: 0.35,
                    minHeight: 30,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  {isAnalyzing ? (
                    <>
                      <CircularProgress size={18} />
                      Re-Analyzing...
                    </>
                  ) : (
                    'Re-Analyze'
                  )}
                </Button>

                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setTicketModalOpen(true)}
                  disabled={isCreatingTicket || isAnalyzing}
                  size="small"
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    px: 1.5,
                    py: 0.35,
                    minHeight: 30,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  Create Jira Ticket
                </Button>
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <JiraTicketModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        analysisData={analysisData}
        isCreatingTicket={isCreatingTicket}
        setIsCreatingTicket={setIsCreatingTicket}
      />
    </>
  );
}
